import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import type { LayoutMode } from './d-video-conference-layout-types.js';

export type { LayoutMode } from './d-video-conference-layout-types.js';

/**
 * Layout component for video conference
 * Handles different layouts based on number of participants and sidebar content
 */
@customElement('d-video-conference-layout')
export class DVideoConferenceLayout extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
      overflow: hidden;
    }

    /* Fullscreen mode - no sidebar (just video) */
    .layout-fullscreen {
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr;
      width: 100%;
      height: 100%;
    }

    /* With sidebar mode - portrait layout with content on top */
    .layout-with-sidebar {
      display: grid;
      grid-template-rows: 60% 40%;
      grid-template-columns: 1fr;
      width: 100%;
      height: 100%;
      gap: 0;
    }

    /* Sidebar area - top 60% in portrait mode */
    .sidebar-area {
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
      overflow-y: auto;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    /* Video area - bottom 40% when sidebar shown, otherwise full screen */
    .video-area {
      display: grid;
      width: 100%;
      height: 100%;
      background: #263238;
      position: relative;
    }

    /* Single participant (just local user waiting) - blank screen with floating local tile */
    .video-area.participants-1 {
      grid-template-rows: 1fr;
      grid-template-columns: 1fr;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .video-area.participants-1 .waiting-message {
      color: rgba(255, 255, 255, 0.6);
      font-size: 18px;
      text-align: center;
    }

    /* Two participants - remote peer fullscreen with floating local tile (no sidebar) */
    .layout-fullscreen .video-area.participants-2 {
      grid-template-rows: 1fr;
      grid-template-columns: 1fr;
      position: relative;
    }

    /* Two participants - grid layout when sidebar is shown (2 columns for comfortable viewing) */
    .layout-with-sidebar .video-area.participants-2 {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      padding: 8px;
    }

    /* Floating tile for local user (only when 1 participant, or 2 participants without sidebar) */
    .video-area.participants-1 .floating-local,
    .layout-fullscreen .video-area.participants-2 .floating-local {
      position: absolute;
      bottom: 20px;
      right: 20px;
      width: 180px;
      height: 240px;
      z-index: 10;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    }

    /* Three or more participants - grid layout with portrait tiles */
    /* Without sidebar: 2 columns with larger tiles */
    .layout-fullscreen .video-area.participants-3,
    .layout-fullscreen .video-area.participants-4,
    .layout-fullscreen .video-area.participants-5,
    .layout-fullscreen .video-area.participants-6 {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      padding: 8px;
    }

    /* With sidebar: 3 columns to fit all participants in 40% space */
    .layout-with-sidebar .video-area.participants-3,
    .layout-with-sidebar .video-area.participants-4,
    .layout-with-sidebar .video-area.participants-5,
    .layout-with-sidebar .video-area.participants-6 {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 6px;
      padding: 6px;
    }

    /* Participant tile - portrait aspect ratio */
    ::slotted([slot^='participant-']) {
      width: 100%;
      height: 100%;
      position: relative;
      border-radius: 8px;
      overflow: hidden;
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      .layout-with-sidebar {
        grid-template-rows: 60% 40%;
        grid-template-columns: 1fr;
      }
    }
  `;

  @property({ type: String })
  layoutMode: LayoutMode = 'fullscreen';

  @property({ type: Number })
  participantCount = 1;

  @property({ type: Boolean })
  showSidebar = false;

  protected render() {
    const layoutClass = this.showSidebar ? 'layout-with-sidebar' : 'layout-fullscreen';
    const videoAreaClass = `video-area participants-${this.participantCount}`;
    const useFloatingForTwo = this.participantCount === 2 && !this.showSidebar;

    return html`
      <div class="${layoutClass}">
        ${this.showSidebar
          ? html`
              <div class="sidebar-area">
                <slot name="sidebar"></slot>
              </div>
            `
          : ''}

        <div class="${videoAreaClass}">
          ${this.participantCount === 1
            ? html`
                <!-- Just local user, waiting for others -->
                <div class="waiting-message">${t('meet.waitingForOthers')}</div>
                <!-- Local user floating -->
                <div class="floating-local">
                  <slot name="participant-0"></slot>
                </div>
              `
            : this.participantCount === 2 && useFloatingForTwo
              ? html`
                  <!-- Remote peer fullscreen (no sidebar) -->
                  <slot name="participant-1"></slot>
                  <!-- Local user floating -->
                  <div class="floating-local">
                    <slot name="participant-0"></slot>
                  </div>
                `
              : html`
                  <!-- Grid layout for 2+ participants (with sidebar) or 3+ participants -->
                  <slot name="participant-0"></slot>
                  <slot name="participant-1"></slot>
                  <slot name="participant-2"></slot>
                  <slot name="participant-3"></slot>
                  <slot name="participant-4"></slot>
                  <slot name="participant-5"></slot>
                `}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-video-conference-layout': DVideoConferenceLayout;
  }
}
